import React from 'react';
import { Box, Flex, VStack, Text, Image } from '@chakra-ui/react';
import { LuQrCode } from 'react-icons/lu';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery } from '../../api';
import IconTextCard from '../../components/base/IconTextCard';
import { useNavigate } from 'react-router-dom';
import { modalManager } from '../../utils/modalManager';
import PageTitle from '../../components/base/PageTitle';
import BookingsList from '../../components/office/BookingsList';
import { PaySubscriptionGate } from '../../components/base/PaySubscriptionGate';
import LoadingState from '../../components/base/LoadingState';
import { canPreviewCoworking } from './coworkingPreview';

const OfficeCategoryCard: React.FC<{
    image: string;
    title: string;
    description: string;
    onClick: () => void;
}> = ({ image, title, description, onClick }) => (
    <Box
        as="button"
        bg="white"
        overflow="hidden"
        cursor="pointer"
        _hover={{
            boxShadow: 'md',
            transform: 'translateY(-2px)',
        }}
        transition="all 0.2s"
        w="full"
        onClick={onClick}
    >
        <VStack gap={0} align="stretch">
            <Box position="relative" w="full" h="200px" overflow="hidden">
                <Image src={image} alt={title} w="full" h="full" objectFit="cover" />
            </Box>
            <VStack gap={2} align="start" p={4}>
                <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                    {title}
                </Text>
                <Text fontSize="sm" color="gray.600">
                    {description}
                </Text>
            </VStack>
        </VStack>
    </Box>
);

const OfficePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: customer, isLoading: isCustomerLoading } = useGetCustomerQuery();

    const handleOpenQrCode = () => {
        if (customer?.status === 'new') {
            modalManager.show({
                title: t.pages.mail.openQrCode,
                message: t.pages.payment.availableAfterPrepayment,
                size: 'sm',
                buttons: [
                    {
                        type: 'success',
                        text: t.pages.payment.pay,
                        onClick: () => {
                            modalManager.remove();
                            navigate('/payment/pay');
                        },
                    },
                ],
            });
            return;
        }
        navigate('/office/access');
    };

    if (isCustomerLoading || !customer) {
        return <LoadingState message={t.pages.mail.loading} />;
    }

    if (customer?.status === 'new') {
        return (
            <Flex flex={1} minH={0} direction="column" overflow="hidden" bg="gray.100">
                <Flex flexShrink={0} justify="center">
                    <PageTitle title={t.pages.office.title} />
                </Flex>
                <PaySubscriptionGate />
            </Flex>
        );
    }

    return (
        <Flex flex={1} minH={0} direction="column" overflow="hidden" bg="gray.100">
            <Flex flexShrink={0} justify="center">
                <PageTitle title={t.pages.office.title} />
            </Flex>
            <Flex
                flex={1}
                minH={0}
                overflowY="auto"
                direction="column"
                w={{ base: 'full', md: '600px' }}
                maxW="4xl"
                mx="auto"
            >
                <VStack px={4} pt={2} pb={4} gap={6} align="stretch" w="full" mb="100px">
                    {/* Блок QR кода - показываем только если есть personal_code */}
                    {customer?.personal_code && customer?.personal_code !== '' && (
                        <IconTextCard
                            icon={LuQrCode}
                            label={t.pages.mail.openQrCode}
                            onClick={handleOpenQrCode}
                        />
                    )}

                    {/* Текущие бронирования */}
                    <BookingsList />

                    <OfficeCategoryCard
                        image="/meeting_room.webp"
                        title={t.pages.office.meetingRooms}
                        description={t.pages.office.meetingRoomsDescription}
                        onClick={() => navigate('/office/meeting-rooms')}
                    />
                    {canPreviewCoworking(customer.id) && (
                        <OfficeCategoryCard
                            image="/meeting2.jpg"
                            title={t.pages.office.coworking}
                            description={t.pages.office.coworkingDescription}
                            onClick={() => navigate('/office/coworking')}
                        />
                    )}
                </VStack>
            </Flex>
        </Flex>
    );
};

export default OfficePage;
